import { useEffect } from 'react';
import { trpc } from '@/lib/trpc';
import { hexToOklch, hexToDesaturatedOklch } from '@/lib/colorUtils';

/**
 * Hook to load tenant configuration and apply dynamic theming
 * 
 * This hook:
 * 1. Fetches tenant config from the API
 * 2. Converts hex colors to HSL format
 * 3. Injects CSS variables into :root
 * 4. Updates favicon and page title
 * 
 * Usage:
 * ```tsx
 * function App() {
 *   const config = useTenantTheme();
 *   // Theme is automatically applied
 * }
 * ```
 */
export function useTenantTheme() {
  const { data: config, isLoading } = trpc.config.getTenantConfig.useQuery(undefined, {
    staleTime: Infinity, // Never refetch - config doesn't change during session
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false, // Don't refetch on reconnect
  });
  
  useEffect(() => {
    // Only apply colors if config is loaded and has branding
    // Don't clear colors if config is undefined (loading/refetching)
    if (!config?.branding) {
      return;
    }
    
    const root = document.documentElement;
    const { primaryColor, secondaryColor } = config.branding;
    
    try {
      // Convert hex colors to OKLCH for Tailwind CSS 4 variables
      // IMPORTANT: Must wrap in oklch() function for Tailwind to recognize
      const primaryOklch = `oklch(${hexToOklch(primaryColor)})`;
      const secondaryOklch = `oklch(${hexToOklch(secondaryColor)})`;
      
      // Inject primary and secondary colors
      root.style.setProperty('--primary', primaryOklch);
      root.style.setProperty('--primary-foreground', 'oklch(100% 0 0)'); // White text on primary
      root.style.setProperty('--secondary', secondaryOklch);
      root.style.setProperty('--secondary-foreground', 'oklch(100% 0 0)'); // White text on secondary
      root.style.setProperty('--sidebar-primary', primaryOklch);
      root.style.setProperty('--sidebar-primary-foreground', 'oklch(100% 0 0)');
      root.style.setProperty('--ring', primaryOklch); // Focus ring color
      root.style.setProperty('--sidebar-ring', primaryOklch);
      root.style.setProperty('--chart-1', primaryOklch);
      root.style.setProperty('--chart-2', secondaryOklch);
      
      // Always use desaturated primary color for sidebar (main brand color)
      const sidebarAccentOklch = `oklch(${hexToDesaturatedOklch(primaryColor, 0.03)})`;
      root.style.setProperty('--sidebar-accent', sidebarAccentOklch);
      root.style.setProperty('--sidebar-accent-foreground', 'oklch(30% 0.05 130)');
    } catch (error) {
    }
    
    // Update favicon if provided
    if (config.branding.favicon) {
      const favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
      if (favicon) {
        favicon.href = config.branding.favicon;
      } else {
        // Create favicon link if it doesn't exist
        const newFavicon = document.createElement('link');
        newFavicon.rel = 'icon';
        newFavicon.href = config.branding.favicon;
        document.head.appendChild(newFavicon);
      }
    }
    
    // Update page title
    if (config.tenantName) {
      document.title = `${config.tenantName} - Kursverwaltung`;
    }
  }, [config]);
  
  return {
    config,
    isLoading,
  };
}
